import React, { useState } from "react";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, Navigate, useLocation } from "react-router-dom";

import MarketingHero from "components/marketing/MarketingHero";
import Address, { AddressData } from "components/address";
import { getAutocompleteType } from "components/contact/ContactForm";
import StepProgress from "components/core/StepProgress";
import auth from "../../services/auth";
import { ErrorResponse } from "../../services/fetcher";
import { useTitle } from "../../utils/UseTitleUtils";
import { AuthData } from "reduxStore/auth";
import {
  AddOnListDto,
  getMarketingBundleTitle,
  getMarketingOrganisationTitle,
  PickPackDto,
  orgPackageDataType,
  pricingPlanDto,
  staffCardListDto,
} from "utils/PricingUtils";
import stripev4, { EstimateRequestDto } from "services/stripev4";
import { GAEvents, eventConstants } from "utils/GoogleAnalytics";

type SignUpInput = {
  firstName?: string;
  lastName?: string;
  email?: string;
  companyName?: string;
  phoneNumber?: string;
  password?: string;
  confirmPassword?: string;
  agreedPrivacyPolicy?: boolean;
  agreedTermsOfService?: boolean;
  stripeCalculationId?: string;
  global?: any;
};

type SignUpError = Omit<
  SignUpInput,
  "agreedPrivacyPolicy" | "agreedTermsOfService"
> & {
  email?: string;
  address?: string;
  agreedPrivacyPolicy?: string;
  agreedTermsOfService?: string;
};

interface SignUpProps {}

const defaultInput: SignUpInput = {
  firstName: "",
  lastName: "",
  email: "",
  companyName: "",
  phoneNumber: "",
  password: "",
  confirmPassword: "",
  agreedPrivacyPolicy: false,
  agreedTermsOfService: false,
};

export const emailRegex =
  /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;

type BundleDataDto = PickPackDto & {
  packData?: pricingPlanDto;
  marketingTitle?: string;
};

type FeatureDataDto = AddOnListDto & {
  packData?: pricingPlanDto;
};

type AccountDataDto = staffCardListDto & {
  packData?: pricingPlanDto;
  maxEmployee?: number;
  count?: number;
};

type OrganisationDataDto = orgPackageDataType & {
  planData?: pricingPlanDto;
  sahRate?: number;
  pahRate?: number;
  nahRate?: number;
  extraSetup?: number;
  extraTraining?: number;
  marketingTitle?: string;
};

type CartDto = {
  selectedOrganisation: OrganisationDataDto | undefined;
  selectedBundle: Array<BundleDataDto> | undefined;
  selectedFeature: Array<FeatureDataDto> | undefined;
  accounts: Array<AccountDataDto> | undefined;
  extras: Array<AccountDataDto> | undefined;
  stripeCalculationId: string;
  estimatedWeeklyTotal?: number;
  permanentWeblinkCount: number;
  temporaryWeblinkCount: number;
};

const SignUp = (props: SignUpProps) => {
  const { state }: { state: CartDto } = useLocation();
  const location = useLocation();

  const [inputs, setInputs] = useState<SignUpInput>(defaultInput);
  const [address, setAddress] = useState<AddressData | null>(null);
  const [errors, setErrors] = useState<SignUpError>({});
  const [isLoading, setIsLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const eventTracker = new GAEvents();

  useTitle("Account & Payment Information");

  if (!state?.selectedOrganisation) {
    return <Navigate to="/pricing" state={{ from: location }} replace />;
  }

  const getErrorId = (field: keyof SignUpError) =>
    errors[field] ? `signup-${field}-error` : undefined;

  const errorMessage = (field: keyof SignUpError) => {
    if (!errors[field]) {
      return null;
    }

    return (
      <div
        id={getErrorId(field)}
        className="marketing-page__error"
        role="alert"
      >
        {errors[field] as any}
      </div>
    );
  };

  const isValid = (event: React.FormEvent<HTMLFormElement>) => {
    const nextErrors: SignUpError = {};

    if (!inputs.firstName?.trim()) {
      nextErrors.firstName = "First name is required.";
      event.stopPropagation();
    }

    if (!inputs.lastName?.trim()) {
      nextErrors.lastName = "Last name is required.";
      event.stopPropagation();
    }

    if (inputs.email?.trim()) {
      if (!emailRegex.test(inputs.email.trim())) {
        nextErrors.email = "Please enter valid Email address.";
        event.stopPropagation();
      }
    } else {
      nextErrors.email = "Email address is required.";
      event.stopPropagation();
    }

    if (!inputs.companyName?.trim()) {
      nextErrors.companyName = "Company name is required.";
      event.stopPropagation();
    }

    if (!inputs.password?.trim()) {
      nextErrors.password = "Password is required.";
      event.stopPropagation();
    }

    if (!inputs.confirmPassword?.trim()) {
      nextErrors.confirmPassword = "Confirm password is required.";
      event.stopPropagation();
    }

    if (
      inputs.password?.trim() &&
      inputs.confirmPassword?.trim() &&
      inputs.password !== inputs.confirmPassword
    ) {
      nextErrors.password = "Passwords don't match";
      event.stopPropagation();
    }

    if (!inputs.agreedPrivacyPolicy) {
      nextErrors.agreedPrivacyPolicy =
        "Please accept the Privacy Policy.";
      event.stopPropagation();
    }

    if (!inputs.agreedTermsOfService) {
      nextErrors.agreedTermsOfService =
        "Please accept the Terms and Conditions.";
      event.stopPropagation();
    }

    if (
      nextErrors.firstName ||
      nextErrors.lastName ||
      nextErrors.companyName ||
      nextErrors.confirmPassword ||
      nextErrors.email ||
      nextErrors.password ||
      nextErrors.agreedPrivacyPolicy ||
      nextErrors.agreedTermsOfService
    ) {
      setErrors(nextErrors);
      return false;
    }

    return true;
  };

  const handleChange =
    (fieldName: keyof SignUpInput) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputs((current) => ({
        ...current,
        [fieldName]: event.target.value,
      }));
      setErrors((current) => {
        const nextErrors = { ...current };
        if (fieldName === "password" || fieldName === "confirmPassword") {
          delete nextErrors.password;
          delete nextErrors.confirmPassword;
        } else {
          delete nextErrors[fieldName];
        }
        delete nextErrors.global;
        return nextErrors;
      });
    };

  const handleCheckboxChange =
    (
      fieldName: "agreedPrivacyPolicy" | "agreedTermsOfService",
      errorField: "agreedPrivacyPolicy" | "agreedTermsOfService",
    ) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputs((current) => ({
        ...current,
        [fieldName]: event.target.checked,
      }));
      setErrors((current) => {
        const nextErrors = { ...current };
        delete nextErrors[errorField];
        return nextErrors;
      });
    };

  const checkout = async (signUpResponse: AuthData) => {
    try {
      setIsLoading(true);
      const accounts = state.accounts || [];
      const extras = state.extras || [];
      const selectedBundle = state.selectedBundle || [];
      const selectedFeature = state.selectedFeature || [];

      const sahCount = accounts.find((e) =>
        e.lookupKey.includes("account_sah_additional"),
      );
      const pahCount = accounts.find((e) =>
        e.lookupKey.includes("account_pah_additional"),
      );
      const nahCount = accounts.find((e) =>
        e.lookupKey.includes("account_nah_additional"),
      );
      const assistedSetup = extras.find((e) => e.lookupKey.includes("extra_setup"));
      const assistedTraining = extras.find((e) =>
        e.lookupKey.includes("extra_training"),
      );

      const payload: EstimateRequestDto = {
        level: state.selectedOrganisation.planData.level,
        bundles: selectedBundle
          .filter((e) => e.lookupKey !== "feature_audit_sharing")
          .map((e) => e.packData.key),
        features: selectedFeature.map((e) => e.packData.key),
        pahCount: pahCount?.count || 0,
        nahCount: nahCount?.count || 0,
        sahCount: sahCount?.count || 0,
        additionalPermanentAuditLinks: state.permanentWeblinkCount,
        additionalTemporaryAuditLinks: state.temporaryWeblinkCount,
        assistedSetup: assistedSetup?.count || 0,
        onsiteTrainingHours: assistedTraining?.count || 0,
        couponId: "",
        country: "AU",
        trial: true,
      };

      const response = await stripev4.stripeCheckout(
        payload,
        signUpResponse.token,
      );

      window.location.href = response.url;
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  const doSignUp = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isValid(event)) {
      return;
    }

    setIsLoading(true);
    auth
      .signup({
        ...inputs,
        companyName: inputs.companyName?.trim() || "New Company",
        address:
          address && (address.country !== "" || address.state !== "")
            ? address
            : null,
        stripeCalculationId: state.stripeCalculationId,
      })
      .then((res) => {
        checkout(res);
        eventTracker.trackFormSubmission(
          eventConstants.CART_CHECKOUT,
          "Continue to Checkout",
          {
            user_email: inputs.email,
          },
        );
      })
      .catch((err: any | ErrorResponse) => {
        if (err.errors) {
          const nextErrors: SignUpError = { ...err.errors };
          if (nextErrors.email) {
            nextErrors.email = (
              <span>
                It seems you already registered here, please{" "}
                <Link to="/auth/login">Login</Link>
              </span>
            ) as any;
          }
          setErrors(nextErrors);
        }

        console.error(err.code, err, err.errors);
        setIsLoading(false);
      });
  };

  const selectedBundle =
    state.selectedBundle?.find((bundle) => bundle.isSelected) ||
    state.selectedBundle?.[0];
  const selectedBundleTitle =
    selectedBundle?.marketingTitle ||
    getMarketingBundleTitle(selectedBundle?.lookupKey) ||
    selectedBundle?.title;
  const selectedOrganisationTitle =
    state.selectedOrganisation.marketingTitle ||
    getMarketingOrganisationTitle(state.selectedOrganisation.lookupKey) ||
    state.selectedOrganisation.title;
  const estimatedWeeklyTotal =
    typeof state.estimatedWeeklyTotal === "number"
      ? state.estimatedWeeklyTotal
      : undefined;
  const isSubmitDisabled =
    isLoading ||
    !inputs.agreedPrivacyPolicy ||
    !inputs.agreedTermsOfService ||
    !inputs.password?.trim() ||
    !inputs.confirmPassword?.trim() ||
    inputs.password?.trim() !== inputs.confirmPassword?.trim();

  return (
    <div className="marketing-page marketing-page--signup">
      <main className="marketing-page__main">
        <MarketingHero title="Account & Payment Information" />

        <section className="marketing-page__section">
          <div className="marketing-page__section-inner">
            <div className="marketing-page__step-progress">
              <StepProgress
                activeIndex={0}
                data={["PURCHASE", "CREATE ACCOUNT", "COMPANY"]}
                label="Checkout progress"
              />
            </div>

            <div className="marketing-page__content-grid">
              <div className="marketing-page__form-panel">
                <div className="marketing-page__copy-stack">
                  <p>To get started you will need to set up your account.</p>
                </div>

                <form className="marketing-page__form" onSubmit={doSignUp}>
                  <div className="marketing-page__split-fields">
                    <div className="marketing-page__field">
                      <label className="marketing-page__label" htmlFor="signup-first-name">
                        First Name
                      </label>
                      <input
                        id="signup-first-name"
                        className="marketing-page__input"
                        type="text"
                        autoComplete={getAutocompleteType("firstName")}
                        value={inputs.firstName}
                        onChange={handleChange("firstName")}
                        aria-invalid={Boolean(errors.firstName)}
                        aria-describedby={getErrorId("firstName")}
                      />
                      {errorMessage("firstName")}
                    </div>

                    <div className="marketing-page__field">
                      <label className="marketing-page__label" htmlFor="signup-last-name">
                        Last Name
                      </label>
                      <input
                        id="signup-last-name"
                        className="marketing-page__input"
                        type="text"
                        autoComplete={getAutocompleteType("lastName")}
                        value={inputs.lastName}
                        onChange={handleChange("lastName")}
                        aria-invalid={Boolean(errors.lastName)}
                        aria-describedby={getErrorId("lastName")}
                      />
                      {errorMessage("lastName")}
                    </div>
                  </div>

                  <div className="marketing-page__field">
                    <label className="marketing-page__label" htmlFor="signup-company-name">
                      Company Name
                    </label>
                    <input
                      id="signup-company-name"
                      className="marketing-page__input"
                      type="text"
                      autoComplete={getAutocompleteType("companyName")}
                      value={inputs.companyName}
                      onChange={handleChange("companyName")}
                      aria-invalid={Boolean(errors.companyName)}
                      aria-describedby={getErrorId("companyName")}
                    />
                    {errorMessage("companyName")}
                  </div>

                  <div className="marketing-page__field">
                    <label className="marketing-page__label" htmlFor="signup-email">
                      Email Address
                    </label>
                    <input
                      id="signup-email"
                      className="marketing-page__input"
                      type="email"
                      autoComplete={getAutocompleteType("email")}
                      value={inputs.email}
                      onChange={handleChange("email")}
                      aria-invalid={Boolean(errors.email)}
                      aria-describedby={getErrorId("email")}
                    />
                    {errorMessage("email")}
                  </div>

                  <div className="marketing-page__field marketing-page__field--business-address">
                    <label className="marketing-page__label" htmlFor="address">
                      Business Address
                    </label>
                    <Address
                      inputLabel=""
                      className="marketing-page__address-autocomplete"
                      required={false}
                      error={errors.address}
                      onAddressChange={(nextAddress) => {
                        setAddress(nextAddress);
                      }}
                      defaultAddress={address}
                    />
                  </div>

                  <div className="marketing-page__field">
                    <label className="marketing-page__label" htmlFor="signup-phone">
                      Phone Number
                    </label>
                    <input
                      id="signup-phone"
                      className="marketing-page__input"
                      type="text"
                      minLength={8}
                      autoComplete={getAutocompleteType("phoneNumber")}
                      value={inputs.phoneNumber}
                      onChange={handleChange("phoneNumber")}
                    />
                  </div>

                  <div className="marketing-page__split-fields">
                    <div className="marketing-page__field">
                      <label className="marketing-page__label" htmlFor="signup-password">
                        Password
                      </label>
                      <div className="input-group">
                        <input
                          id="signup-password"
                          className="marketing-page__input form-control"
                          type={passwordVisible ? "text" : "password"}
                          autoComplete={getAutocompleteType("password")}
                          value={inputs.password}
                          onChange={handleChange("password")}
                          aria-invalid={Boolean(errors.password)}
                          aria-describedby={getErrorId("password")}
                        />
                        <button
                          className="input-group-text"
                          aria-label="Toggle password visibility"
                          title="toggle visibility"
                          type="button"
                          onClick={() => setPasswordVisible((current) => !current)}
                        >
                          <FontAwesomeIcon
                            icon={passwordVisible ? faEyeSlash : faEye}
                          />
                        </button>
                      </div>
                      {errorMessage("password")}
                    </div>

                    <div className="marketing-page__field">
                      <label
                        className="marketing-page__label"
                        htmlFor="signup-confirm-password"
                      >
                        Confirm Password
                      </label>
                      <div className="input-group">
                        <input
                          id="signup-confirm-password"
                          className="marketing-page__input form-control"
                          type={confirmPasswordVisible ? "text" : "password"}
                          autoComplete={getAutocompleteType("confirmPassword")}
                          value={inputs.confirmPassword}
                          onChange={handleChange("confirmPassword")}
                          aria-invalid={Boolean(errors.confirmPassword)}
                          aria-describedby={getErrorId("confirmPassword")}
                        />
                        <button
                          className="input-group-text"
                          aria-label="Toggle confirm password visibility"
                          title="toggle visibility"
                          type="button"
                          onClick={() =>
                            setConfirmPasswordVisible((current) => !current)
                          }
                        >
                          <FontAwesomeIcon
                            icon={confirmPasswordVisible ? faEyeSlash : faEye}
                          />
                        </button>
                      </div>
                      {errorMessage("confirmPassword")}
                    </div>
                  </div>

                  <div className="marketing-page__checkbox-stack">
                    <label
                      className="marketing-page__checkbox-inline"
                      htmlFor="signup-privacy-policy"
                    >
                      <input
                        id="signup-privacy-policy"
                        type="checkbox"
                        checked={Boolean(inputs.agreedPrivacyPolicy)}
                        onChange={handleCheckboxChange(
                          "agreedPrivacyPolicy",
                          "agreedPrivacyPolicy",
                        )}
                        aria-invalid={Boolean(errors.agreedPrivacyPolicy)}
                        aria-describedby={getErrorId("agreedPrivacyPolicy")}
                      />
                      <span>
                        I have read and agree to the{" "}
                        <Link to="/privacy-policy" target="_blank" rel="noreferrer">
                          Privacy Policy
                        </Link>
                      </span>
                    </label>
                    {errorMessage("agreedPrivacyPolicy")}

                    <label
                      className="marketing-page__checkbox-inline"
                      htmlFor="signup-terms-and-condition"
                    >
                      <input
                        id="signup-terms-and-condition"
                        type="checkbox"
                        checked={Boolean(inputs.agreedTermsOfService)}
                        onChange={handleCheckboxChange(
                          "agreedTermsOfService",
                          "agreedTermsOfService",
                        )}
                        aria-invalid={Boolean(errors.agreedTermsOfService)}
                        aria-describedby={getErrorId("agreedTermsOfService")}
                      />
                      <span>
                        I have read and agree to the{" "}
                        <Link to="/terms" target="_blank" rel="noreferrer">
                          Terms and Conditions
                        </Link>
                      </span>
                    </label>
                    {errorMessage("agreedTermsOfService")}
                  </div>

                  <button
                    title="confirm"
                    aria-label="Confirm Pay"
                    type="submit"
                    disabled={isSubmitDisabled}
                    className="marketing__button marketing__button--primary marketing-page__full-button"
                  >
                    {isLoading ? "Redirecting..." : "Confirm & Pay"}
                  </button>
                </form>
              </div>

              <aside className="marketing-page__sidebar">
                <h2 className="marketing-page__sidebar-title">
                  Your selected plan
                </h2>
                <div className="marketing-page__sidebar-items">
                  <div className="marketing-page__sidebar-item">
                    <p>
                      <strong>{selectedOrganisationTitle}</strong>
                      {selectedBundleTitle ? (
                        <>
                          <br />
                          {selectedBundleTitle}
                        </>
                      ) : null}
                    </p>
                  </div>
                  {typeof estimatedWeeklyTotal === "number" && (
                    <div className="marketing-page__sidebar-item">
                      <div className="marketing-page__sidebar-price">
                        <p className="marketing-page__sidebar-price-trial">
                          <span className="marketing-page__sidebar-price-free">
                            FREE
                          </span>{" "}
                          <span className="marketing-page__sidebar-price-copy">
                            for the first week
                          </span>
                        </p>
                        <p className="marketing-page__sidebar-price-value">
                          ${(estimatedWeeklyTotal / 100).toFixed(2)}
                          <span className="marketing-page__sidebar-price-suffix">
                            /wk after
                          </span>
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="marketing-page__sidebar-item">
                    <p>Secure Stripe checkout will open after signup.</p>
                  </div>
                  <div className="marketing-page__sidebar-item">
                    <p>
                      Already registered? <Link to="/auth/login">Login here</Link>
                    </p>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SignUp;

export { SignUpInput };
